import { ChartLineUp, TrendUp, TrendDown } from "@phosphor-icons/react";

/**
 * KpiBand — headline "how are we doing this week?" panel at the top of
 * the dashboard. Four KPIs with week-over-week deltas, plus a top-source
 * leaderboard tile so the winning bio channel jumps out.
 */
export function KpiBand({ comparison }) {
  if (!comparison) return null;

  const kpis = [
    { key: "signups", label: "Signups", data: comparison.signups },
    { key: "installs", label: "Installs", data: comparison.installs },
    { key: "download_clicks", label: "App Store clicks", data: comparison.download_clicks },
    { key: "page_views", label: "Page views", data: comparison.page_views },
  ];

  const topSources = comparison.top_sources || [];
  const winner = topSources[0];
  const totalWeekSignups = topSources.reduce((sum, s) => sum + (s.count || 0), 0);

  return (
    <section data-testid="admin-kpi-band" className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-xl font-extrabold tracking-tight">
          This week at a glance
        </h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
          last 7 days · vs prior 7
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {kpis.map(({ key, label, data }) => (
          <KpiTile key={key} label={label} data={data} testId={`kpi-${key}`} />
        ))}

        {/* Leaderboard tile */}
        <div
          className="border border-[#FF2A2A]/30 bg-gradient-to-br from-[#FF2A2A]/[0.08] via-[#0a0a0a] to-[#0a0a0a] p-5 col-span-2 lg:col-span-1"
          data-testid="admin-source-leaderboard"
        >
          <div className="flex items-center gap-2 text-[#FF2A2A] mb-2">
            <ChartLineUp size={16} weight="duotone" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
              top source
            </span>
          </div>
          {winner ? (
            <>
              <div
                className="font-display font-extrabold tracking-tight text-white text-lg truncate"
                title={winner.source}
                data-testid="admin-leaderboard-winner-source"
              >
                {winner.source}
              </div>
              <div
                className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/50"
                data-testid="admin-leaderboard-winner-count"
              >
                {winner.count} of {totalWeekSignups} signup
                {totalWeekSignups === 1 ? "" : "s"}
              </div>
              {topSources.length > 1 ? (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
                  {topSources.slice(1, 4).map((s) => (
                    <div
                      key={s.source}
                      className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-white/50"
                    >
                      <span className="truncate mr-2" title={s.source}>
                        {s.source}
                      </span>
                      <span className="text-white/70 tabular-nums">
                        {s.count}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
              no signups this week
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function KpiTile({ label, data, testId }) {
  const value = data?.this ?? 0;
  const pct = data?.pct;
  const hasChange = pct !== null && pct !== undefined;
  const positive = hasChange && pct > 0;
  const negative = hasChange && pct < 0;
  const deltaColor = positive
    ? "text-emerald-400"
    : negative
      ? "text-[#FF2A2A]"
      : "text-white/40";
  const Arrow = positive ? TrendUp : negative ? TrendDown : null;

  return (
    <div
      className="border border-white/10 bg-[#0a0a0a] p-5"
      data-testid={testId}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2">
        {label}
      </div>
      <div
        className="font-display text-3xl font-extrabold tracking-tighter text-white tabular-nums"
        data-testid={`${testId}-value`}
      >
        {value}
      </div>
      <div
        className={`mt-1 flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] ${deltaColor}`}
        data-testid={`${testId}-delta`}
      >
        {Arrow ? <Arrow size={11} weight="bold" /> : null}
        {hasChange ? (
          <span>
            {positive ? "+" : ""}
            {pct}% vs last wk
          </span>
        ) : (
          <span>no change</span>
        )}
      </div>
    </div>
  );
}
